import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalization } from "../contexts/LocalizationContext";
import {
  Lock,
  AlertCircle,
  Globe,
  ChevronDown,
  Shield,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language, setLanguage } = useLocalization();

  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const tokenMissing = !token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError(language === "fr"
        ? "Le mot de passe doit contenir au moins 8 caracteres."
        : "Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError(language === "fr"
        ? "Les mots de passe ne correspondent pas."
        : "Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!res.ok) {
        let detail = "";
        try {
          const j = await res.json();
          detail = j?.detail || j?.message || "";
        } catch {
          // ignore
        }
        if (!detail) {
          detail = language === "fr"
            ? "Lien invalide ou expire. Demandez un nouveau lien."
            : "Invalid or expired link. Please request a new one.";
        }
        setError(detail);
        return;
      }
      setSuccess(true);
    } catch {
      setError(language === "fr"
        ? "Une erreur reseau est survenue. Veuillez reessayer."
        : "A network error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col relative overflow-hidden">
      {/* Background dots */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, rgba(99,102,241,0.4) 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }} />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-5">
        <div className="flex items-center gap-2.5">
          <img src="/LOGO_MOKO_fd_vert.png" alt="MOKO Afrika" className="h-10 w-10 rounded-xl shadow-md object-cover"/>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-900 leading-tight">MOKO Afrika</span>
            <span className="text-[9px] text-indigo-500 font-bold tracking-[0.15em] uppercase">Merchant Suite</span>
          </div>
        </div>

        <div className="relative z-50">
          <button
            type="button"
            onClick={() => setLangMenuOpen(!langMenuOpen)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 bg-white text-sm font-medium text-slate-600 hover:text-slate-900 transition-all shadow-sm"
          >
            <Globe className="w-4 h-4" />
            <span>{language === "fr" ? "FR" : "EN"}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {langMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setLangMenuOpen(false)} />
              <div className="absolute right-0 mt-2 w-44 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
                <button type="button" onClick={() => { setLanguage("fr"); setLangMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${language === "fr" ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
                  <span className="text-base">🇫🇷</span> {t("language.french") || "Francais"}
                </button>
                <button type="button" onClick={() => { setLanguage("en"); setLangMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${language === "en" ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
                  <span className="text-base">🇬🇧</span> {t("language.english") || "English"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 pb-10">
        <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2 tracking-tight">
                {language === "fr" ? "Mot de passe mis a jour !" : "Password updated!"}
              </h1>
              <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                {language === "fr"
                  ? "Votre nouveau mot de passe est actif. Vous pouvez vous connecter."
                  : "Your new password is active. You can now log in."}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-3.5 rounded-xl font-semibold text-[15px] text-white flex items-center justify-center gap-2 group"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)' }}
              >
                <span>{language === "fr" ? "Aller a la connexion" : "Go to login"}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-5">
                  <KeyRound className="w-7 h-7 text-indigo-500" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 tracking-tight">
                  {language === "fr" ? "Nouveau mot de passe" : "New password"}
                </h1>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {language === "fr"
                    ? "Choisissez un mot de passe d'au moins 8 caracteres."
                    : "Choose a password with at least 8 characters."}
                </p>
              </div>

              {tokenMissing && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-amber-800 leading-snug">
                    {language === "fr"
                      ? "Aucun token dans le lien. Demandez un nouveau lien depuis la page Mot de passe oublie."
                      : "No token in the link. Please request a new link from the Forgot password page."}
                  </span>
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                  </div>
                  <span className="text-sm text-red-700 leading-snug">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-[13px] font-semibold text-slate-700 mb-2">
                    {language === "fr" ? "Nouveau mot de passe" : "New password"}
                  </label>
                  <div className="relative group">
                    <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center pointer-events-none">
                      <Lock className="w-[18px] h-[18px] text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                    </div>
                    <input
                      type={showPwd ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3.5 bg-slate-50/80 border border-slate-200 rounded-xl text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      tabIndex={-1}
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-slate-700 mb-2">
                    {language === "fr" ? "Confirmer le mot de passe" : "Confirm password"}
                  </label>
                  <div className="relative group">
                    <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center pointer-events-none">
                      <Lock className="w-[18px] h-[18px] text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                    </div>
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full pl-12 pr-12 py-3.5 bg-slate-50/80 border border-slate-200 rounded-xl text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      tabIndex={-1}
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || tokenMissing}
                  className="w-full relative py-3.5 rounded-xl font-semibold text-[15px] text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2 group overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)' }}
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 relative" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="relative">{language === "fr" ? "Mise a jour..." : "Updating..."}</span>
                    </>
                  ) : (
                    <>
                      <span className="relative">{language === "fr" ? "Reinitialiser" : "Reset password"}</span>
                      <ArrowRight className="w-4 h-4 relative group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <button
                onClick={() => navigate("/login")}
                className="w-full mt-4 py-3 rounded-xl font-medium text-[14px] text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all flex items-center justify-center gap-2 group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span>{language === "fr" ? "Retour a la connexion" : "Back to login"}</span>
              </button>

              <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-400">
                <Shield className="w-3 h-3" />
                <span>{t("login.encrypted") || "SSL Encrypted"}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="relative z-10 text-center pb-5 text-xs text-slate-400">
        &copy; {new Date().getFullYear()} MOKO Afrika. {t("login.allRightsReserved") || "All rights reserved."}
      </div>
    </div>
  );
};

export default ResetPassword;
